// src/theme.ts
import { createTheme } from '@mui/material/styles';
import { alpha } from '@mui/material';

const theme = createTheme({
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1536,
    },
  },
  palette: {
    mode: 'light',
    primary: {
      main: '#2E7D32', // Darker green for better contrast
      light: '#66BB6A',
      dark: '#1B5E20',
      contrastText: '#fff',
    },
    secondary: {
      main: '#FF9800', // Brighter orange for better visibility
      light: '#FFB74D',
      dark: '#F57C00',
      contrastText: '#fff',
    },
    error: {
      main: '#D32F2F', // Stronger red
      light: '#EF5350',
      dark: '#C62828',
    },
    warning: {
      main: '#ED6C02', // Stronger orange
      light: '#FF9800',
      dark: '#E65100',
    },
    info: {
      main: '#0288D1', // Stronger blue
      light: '#03A9F4',
      dark: '#01579B',
    },
    success: {
      main: '#2E7D32', // Forest green
      light: '#66BB6A',
      dark: '#1B5E20',
    },
    background: {
      default: '#F5F7F9', // Subtle light gray
      paper: '#FFFFFF',
    },
    grey: {
      50: '#FAFAFA',
      100: '#F5F5F5',
      200: '#EEEEEE',
      300: '#E0E0E0',
      400: '#BDBDBD',
      500: '#9E9E9E',
      600: '#757575',
      700: '#616161',
      800: '#424242',
      900: '#212121',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontSize: '2.5rem',
      fontWeight: 800,
      letterSpacing: '-0.02em',
      lineHeight: 1.2,
    },
    h2: {
      fontSize: '2rem',
      fontWeight: 700,
      letterSpacing: '-0.01em',
      lineHeight: 1.3,
    },
    h3: {
      fontSize: '1.75rem',
      fontWeight: 600,
      lineHeight: 1.3,
    },
    h4: {
      fontSize: '1.5rem',
      fontWeight: 600,
      lineHeight: 1.35,
    },
    h5: {
      fontSize: '1.25rem',
      fontWeight: 500,
      lineHeight: 1.4,
    },
    h6: {
      fontSize: '1rem',
      fontWeight: 500,
      lineHeight: 1.4,
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.5,
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.45,
    },
    subtitle1: {
      fontSize: '0.9rem',
      fontWeight: 400,
      color: '#546E7A',
    },
    button: {
      textTransform: 'none',
      fontWeight: 600,
    },
  },
  shape: {
    borderRadius: 12,
  },
  spacing: 8,
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        html: {
          height: '100%',
        },
        body: {
          height: '100%',
          minHeight: '100vh',
          margin: 0,
          padding: 0,
          backgroundColor: '#F5F7F9',
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
        },
        '#root': {
          minHeight: '100%',
          display: 'flex',
          flexDirection: 'column',
        },
        footer: {
          backgroundColor: '#263238',
          color: '#fff',
          textAlign: 'center',
          padding: '24px',
          position: 'relative',
          left: 0,
          margin: 0,
          width: '100%',
          boxSizing: 'border-box',
        },
        a: {
          textDecoration: 'none',
          color: 'inherit',
        },
        '::-webkit-scrollbar': {
          width: '8px',
          height: '8px',
        },
        '::-webkit-scrollbar-track': {
          background: '#f1f1f1',
        },
        '::-webkit-scrollbar-thumb': {
          background: '#c1c1c1',
          borderRadius: '4px',
        },
        '::-webkit-scrollbar-thumb:hover': {
          background: '#a8a8a8',
        },
      },
    },
    MuiContainer: {
      styleOverrides: {
        root: {
          backgroundColor: 'transparent',
          border: 'none',
          boxShadow: 'none',
          margin: '0 auto',
          padding: '24px',
          maxWidth: '1440px', // Wider container for better use of screen space
          '&::before': { display: 'none' },
          '&::after': { display: 'none' },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: 'white',
          boxShadow: '0 1px 3px rgba(0,0,0,0.05), 0 1px 2px rgba(0,0,0,0.1)',
          border: 'none',
        },
        elevation1: {
          boxShadow: '0 1px 3px rgba(0,0,0,0.05), 0 1px 2px rgba(0,0,0,0.1)',
        },
        elevation2: {
          boxShadow: '0 3px 6px rgba(0,0,0,0.08), 0 3px 6px rgba(0,0,0,0.12)',
        },
        elevation3: {
          boxShadow: '0 10px 20px rgba(0,0,0,0.1), 0 6px 6px rgba(0,0,0,0.12)',
        },
        elevation4: {
          boxShadow: '0 14px 28px rgba(0,0,0,0.15), 0 10px 10px rgba(0,0,0,0.12)',
        },
      },
    },
    MuiAppBar: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          borderBottom: 'none',
          '&::before': { display: 'none' },
          '&::after': { display: 'none' },
          background: 'linear-gradient(90deg, #2E7D32 0%, #1B5E20 100%)',
          color: '#fff',
          position: 'relative',
          minHeight: 70,
          zIndex: 1100,
        },
      },
    },
    MuiToolbar: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
          borderBottom: 'none',
          '&::before': { display: 'none' },
          '&::after': { display: 'none' },
          minHeight: '70px !important',
          '@media (min-width: 600px)': {
            minHeight: '70px !important',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          padding: '8px 20px',
          borderRadius: 8,
          transition: 'all 0.2s ease',
        },
        contained: {
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          '&:hover': {
            boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
            transform: 'translateY(-1px)',
          },
        },
        containedPrimary: {
          background: 'linear-gradient(90deg, #2E7D32 0%, #1B5E20 100%)',
          '&:hover': {
            background: 'linear-gradient(90deg, #388E3C 0%, #2E7D32 100%)',
          },
        },
        containedSecondary: {
          background: 'linear-gradient(90deg, #FF9800 0%, #F57C00 100%)',
          '&:hover': {
            background: 'linear-gradient(90deg, #FFA726 0%, #FF9800 100%)',
          },
        },
        text: {
          padding: '6px 16px',
        },
        outlined: {
          borderWidth: '1.5px',
          '&:hover': {
            borderWidth: '1.5px',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: 'white',
          borderRadius: 16,
          border: 'none !important',
          outline: 'none !important',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
          transition: 'transform 0.25s ease, box-shadow 0.25s ease',
          overflow: 'visible',
          '&:hover': {
            transform: 'translateY(-5px)',
            boxShadow: '0 12px 28px rgba(0, 0, 0, 0.15)',
          },
          // Remove border if variant="outlined"
          '&.MuiPaper-outlined': {
            border: 'none !important',
            outline: 'none !important',
          },
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: '24px',
          '&:last-child': {
            paddingBottom: '24px',
          },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          backgroundColor: '#fafafa',
          transition: 'box-shadow 0.2s ease, background-color 0.2s ease',
          '&.Mui-focused': {
            backgroundColor: '#ffffff',
            boxShadow: `0px 0px 0px 2px ${alpha('#2E7D32', 0.2)}`,
          },
        },
      },
    },
    MuiFilledInput: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          backgroundColor: '#f5f5f5',
          '&:hover': {
            backgroundColor: '#f0f0f0',
          },
          '&.Mui-focused': {
            backgroundColor: '#ffffff',
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: alpha('#2E7D32', 0.5),
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderWidth: '2px',
          },
        },
        notchedOutline: {
          borderColor: '#e0e0e0',
          transition: 'border-color 0.2s ease',
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          fontWeight: 500,
        },
        filled: {
          background: alpha('#2E7D32', 0.1),
          color: '#2E7D32',
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          '&.Mui-selected': {
            backgroundColor: alpha('#2E7D32', 0.1),
            '&:hover': {
              backgroundColor: alpha('#2E7D32', 0.15),
            },
          },
        },
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          '&.Mui-selected': {
            backgroundColor: alpha('#2E7D32', 0.1),
            '&:hover': {
              backgroundColor: alpha('#2E7D32', 0.15),
            },
          },
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: '#f0f0f0',
        },
      },
    },
    MuiIcon: {
      styleOverrides: {
        root: {
          transition: 'color 0.2s ease',
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: '#263238',
          color: '#fff',
          fontSize: '0.75rem',
          borderRadius: 4,
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
        },
        arrow: {
          color: '#263238',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
        },
      },
    },
  },
});

export default theme;
